/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { TrainingModes, TrainingState, TrainingStatus } from 'src/luis/types/trainingTypes';
import { TrainingServiceStatus } from 'src/modules/modelTraining/types/trainingTypes';

type TrainingStoreData = Pick<TrainingStore, 'isTrained' | 'currentTrainingStatus' | 'lastTrainingMode'>;

export class TrainingStore {
    @observable public isTrained: boolean;

    @observable public lastTrainingTime: Date;

    @observable public lastTrainingMode: TrainingModes;

    @observable public lastTrainingStatus: TrainingStatus;

    @observable public currentTrainingStatus: TrainingStatus;

    constructor(initialData?: TrainingStoreData) {
        this.isTrained = initialData?.isTrained ?? false;
        this.currentTrainingStatus = initialData?.currentTrainingStatus;
        this.lastTrainingMode = initialData?.lastTrainingMode ?? 'Standard';
    }

    @computed
    public get serviceStatus(): TrainingServiceStatus {
        const readyTrainStatus: TrainingState[] = ['none', 'upToDate', 'success', 'fail'];
        const inProgressTrainStatus: TrainingState[] = ['queued', 'collectingData', 'inProgress', 'sampling', 'uploadingModel'];

        if (readyTrainStatus.some(s => s === this.currentTrainingStatus?.overallStatus)) {
            return 'ready';
        }

        if (inProgressTrainStatus.some(s => s === this.currentTrainingStatus?.overallStatus)) {
            return 'inProgress';
        }

        return 'ready';
    }

    /**
     * Sets the `isTrained` boolean value with the given value.
     *
     * @param isTrainedVal The boolean value to be set.
     */
    @action
    public setIsTrained(isTrained: boolean): void {
        this.isTrained = isTrained;
    }

    /**
     * Sets the `status` enum value with the given value.
     *
     * @param status The enum value to be set.
     */
    @action
    public setCurrentTrainingStatus(status: TrainingStatus): void {
        this.currentTrainingStatus = status;
    }

    /**
     * Sets the `status` enum value with the given value.
     *
     * @param status The enum value to be set.
     */
    @action
    public setLastTrainingStatus(status: TrainingStatus): void {
        this.lastTrainingStatus = status;
    }

    /**
     * Sets the mode value with the given value.
     */
    @action
    public setLastTrainingMode(mode: TrainingModes): void {
        this.lastTrainingMode = mode;
    }

    /**
     * Sets the `time` date value with the given value.
     *
     * @param time The date value to be set.
     */
    @action
    public setLastTrainingTime(time: Date): void {
        this.lastTrainingTime = time;
    }
}
